import React, { useMemo, useState } from 'react'
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Alert,
  Tabs,
  Statistic,
  Row,
  Col,
  message,
} from 'antd'
import { SearchOutlined, ReloadOutlined, ClearOutlined, CheckOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@services/api'

const { Search } = Input
const { Option } = Select

interface LogItem {
  id?: number
  timestamp: string
  level: string
  logger_name?: string
  message: string
  extra_data?: Record<string, any>
}

interface TradeEventItem {
  id: number
  occurred_at: string
  event_type: string
  source: string
  token_id?: number
  token_name?: string
  exchange?: string
  symbol?: string
  side?: string
  contracts?: number
  entry_price?: number
  exit_price?: number
  realized_pnl?: number
  commission?: number
  order_id?: string
  signal_id?: string
  order_type?: string
  holding_hours?: number
  status?: string
  error_message?: string
}

interface ExceptionEventItem {
  id: number
  occurred_at: string
  severity: string
  category: string
  error_type?: string
  message: string
  token_id?: number
  signal_id?: string
  endpoint?: string
  resolved: boolean
  resolved_at?: string
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  open: '开仓',
  close: '平仓',
  partial_close: '部分平仓',
  open_failed: '开仓失败',
  close_failed: '平仓失败',
}

const SOURCE_LABELS: Record<string, string> = {
  webhook: 'Webhook',
  manual: '手动',
  stop_loss: '止损',
  limit_close: '限价平仓',
}

const getLevelColor = (level: string) => {
  switch (level) {
    case 'DEBUG': return 'default'
    case 'INFO': return 'blue'
    case 'WARNING': return 'orange'
    case 'ERROR': return 'red'
    case 'CRITICAL': return 'magenta'
    default: return 'default'
  }
}

const SystemLogsTab: React.FC = () => {
  const [searchText, setSearchText] = useState('')
  const [selectedLevel, setSelectedLevel] = useState<string | undefined>(undefined)
  const [selectedModule, setSelectedModule] = useState<string | undefined>(undefined)

  const { data: logs = [], isLoading, isError, refetch } = useQuery<LogItem[]>({
    queryKey: ['logs', 'system'],
    queryFn: () => api.logs.getLogs({ limit: 500 }),
    refetchInterval: 10000,
  })

  const filteredLogs = useMemo(() => {
    let filtered = logs
    if (searchText.trim()) {
      const search = searchText.toLowerCase()
      filtered = filtered.filter((log) =>
        log.message?.toLowerCase().includes(search) ||
        String(log.extra_data?.event_type || '').toLowerCase().includes(search) ||
        String(log.logger_name || '').toLowerCase().includes(search)
      )
    }
    if (selectedLevel) {
      filtered = filtered.filter((log) => (log.level || '').toUpperCase() === selectedLevel)
    }
    if (selectedModule) {
      filtered = filtered.filter((log) => (log.logger_name || '') === selectedModule)
    }
    return filtered
  }, [logs, searchText, selectedLevel, selectedModule])

  const moduleOptions = useMemo(
    () => Array.from(new Set(logs.map((log) => log.logger_name).filter(Boolean))) as string[],
    [logs]
  )

  const clearFilters = () => {
    setSearchText('')
    setSelectedLevel(undefined)
    setSelectedModule(undefined)
  }

  const columns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp: string) => new Date(timestamp).toLocaleString(),
    },
    {
      title: '级别',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level: string) => <Tag color={getLevelColor(level)}>{level}</Tag>,
    },
    {
      title: '模块',
      dataIndex: 'logger_name',
      key: 'logger_name',
      width: 120,
      render: (module: string) => <Tag color="blue">{module}</Tag>,
    },
    {
      title: '事件类型',
      dataIndex: 'extra_data',
      key: 'event_type',
      width: 150,
      render: (extraData?: Record<string, any>) => extraData?.event_type || '-',
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
  ]

  return (
    <Card
      extra={
        <Space>
          <Button icon={<ClearOutlined />} onClick={clearFilters}>清空筛选</Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>刷新</Button>
        </Space>
      }
    >
      {isError && (
        <Alert type="error" showIcon message="日志加载失败" style={{ marginBottom: 16 }} />
      )}
      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <Search
            placeholder="搜索日志消息或事件类型"
            allowClear
            style={{ width: 300 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
          />
          <Select
            placeholder="日志级别"
            style={{ width: 120 }}
            allowClear
            value={selectedLevel}
            onChange={setSelectedLevel}
          >
            <Option value="DEBUG">DEBUG</Option>
            <Option value="INFO">INFO</Option>
            <Option value="WARNING">WARNING</Option>
            <Option value="ERROR">ERROR</Option>
            <Option value="CRITICAL">CRITICAL</Option>
          </Select>
          <Select
            placeholder="模块"
            style={{ width: 160 }}
            allowClear
            value={selectedModule}
            onChange={setSelectedModule}
          >
            {moduleOptions.map((name) => (
              <Option key={name} value={name}>{name}</Option>
            ))}
          </Select>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredLogs}
        loading={isLoading}
        rowKey={(record) => `${record.id ?? 'no-id'}-${record.timestamp}`}
        pagination={{ showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
        size="small"
      />
    </Card>
  )
}

const TradeLogsTab: React.FC = () => {
  const [symbol, setSymbol] = useState('')
  const [eventType, setEventType] = useState<string | undefined>(undefined)
  const [source, setSource] = useState<string | undefined>(undefined)

  const { data: events = [], isLoading, isError, refetch } = useQuery<TradeEventItem[]>({
    queryKey: ['logs', 'trades', symbol, eventType, source],
    queryFn: () =>
      api.logs.getTradeEvents({
        limit: 500,
        symbol: symbol || undefined,
        event_type: eventType,
        source,
      }),
    refetchInterval: 10000,
  })

  const { data: summary } = useQuery({
    queryKey: ['logs', 'trades', 'summary', symbol, eventType, source],
    queryFn: () =>
      api.logs.getTradeSummary({
        symbol: symbol || undefined,
        event_type: eventType,
        source,
      }),
    refetchInterval: 10000,
  })

  const columns = [
    {
      title: '时间',
      dataIndex: 'occurred_at',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'Token',
      dataIndex: 'token_name',
      width: 100,
      render: (name: string, row: TradeEventItem) => name || (row.token_id ? `#${row.token_id}` : '-'),
    },
    { title: '交易对', dataIndex: 'symbol', width: 110 },
    { title: '方向', dataIndex: 'side', width: 70 },
    {
      title: '事件',
      dataIndex: 'event_type',
      width: 100,
      render: (v: string) => <Tag>{EVENT_TYPE_LABELS[v] || v}</Tag>,
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 90,
      render: (v: string) => SOURCE_LABELS[v] || v,
    },
    {
      title: '数量',
      dataIndex: 'contracts',
      width: 90,
      render: (v?: number) => (v != null ? Number(v).toFixed(4) : '-'),
    },
    {
      title: '开仓价',
      dataIndex: 'entry_price',
      width: 90,
      render: (v?: number) => (v != null ? Number(v).toFixed(4) : '-'),
    },
    {
      title: '平仓价',
      dataIndex: 'exit_price',
      width: 90,
      render: (v?: number) => (v != null ? Number(v).toFixed(4) : '-'),
    },
    {
      title: '盈亏',
      dataIndex: 'realized_pnl',
      width: 90,
      render: (v?: number) => {
        if (v == null) return '-'
        const n = Number(v)
        return <span style={{ color: n >= 0 ? '#3f8600' : '#cf1322' }}>{n.toFixed(2)}</span>
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (v?: string) => (
        <Tag color={v === 'success' ? 'green' : v === 'failed' ? 'red' : 'default'}>{v || '-'}</Tag>
      ),
    },
    {
      title: '信号/订单',
      key: 'refs',
      width: 140,
      ellipsis: true,
      render: (_: unknown, row: TradeEventItem) => row.signal_id || row.order_id || '-',
    },
  ]

  return (
    <div>
      {summary && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}><Statistic title="总事件" value={summary.total_count} /></Col>
          <Col span={4}><Statistic title="开仓" value={summary.open_count} /></Col>
          <Col span={4}><Statistic title="平仓" value={summary.close_count} /></Col>
          <Col span={4}><Statistic title="部分平仓" value={summary.partial_close_count} /></Col>
          <Col span={4}>
            <Statistic
              title="总盈亏"
              value={summary.total_realized_pnl != null ? Number(summary.total_realized_pnl).toFixed(2) : '-'}
            />
          </Col>
        </Row>
      )}
      <Card
        extra={<Button icon={<ReloadOutlined />} onClick={() => refetch()}>刷新</Button>}
      >
        {isError && (
          <Alert type="error" showIcon message="交易日志加载失败" style={{ marginBottom: 16 }} />
        )}
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder="交易对"
            allowClear
            style={{ width: 140 }}
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
          />
          <Select
            placeholder="事件类型"
            allowClear
            style={{ width: 130 }}
            value={eventType}
            onChange={setEventType}
          >
            {Object.entries(EVENT_TYPE_LABELS).map(([k, label]) => (
              <Option key={k} value={k}>{label}</Option>
            ))}
          </Select>
          <Select
            placeholder="来源"
            allowClear
            style={{ width: 120 }}
            value={source}
            onChange={setSource}
          >
            {Object.entries(SOURCE_LABELS).map(([k, label]) => (
              <Option key={k} value={k}>{label}</Option>
            ))}
          </Select>
        </Space>
        <Table
          columns={columns}
          dataSource={events}
          loading={isLoading}
          rowKey="id"
          pagination={{ showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
          size="small"
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  )
}

const ExceptionLogsTab: React.FC = () => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [severity, setSeverity] = useState<string | undefined>(undefined)
  const [resolved, setResolved] = useState<boolean | undefined>(undefined)

  const { data: events = [], isLoading, isError, refetch } = useQuery<ExceptionEventItem[]>({
    queryKey: ['logs', 'exceptions', search, severity, resolved],
    queryFn: () =>
      api.logs.getExceptionEvents({
        limit: 500,
        search: search || undefined,
        severity,
        resolved,
      }),
    refetchInterval: 10000,
  })

  const resolveMutation = useMutation({
    mutationFn: (id: number) => api.logs.resolveException(id),
    onSuccess: () => {
      message.success('已标记为已处理')
      queryClient.invalidateQueries({ queryKey: ['logs', 'exceptions'] })
    },
    onError: () => message.error('标记失败'),
  })

  const columns = [
    {
      title: '时间',
      dataIndex: 'occurred_at',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: '级别',
      dataIndex: 'severity',
      width: 90,
      render: (v: string) => <Tag color={getLevelColor(v.toUpperCase())}>{v}</Tag>,
    },
    { title: '分类', dataIndex: 'category', width: 100 },
    { title: '类型', dataIndex: 'error_type', width: 120, ellipsis: true },
    { title: '消息', dataIndex: 'message', ellipsis: true },
    {
      title: 'Token',
      dataIndex: 'token_id',
      width: 80,
      render: (v?: number) => (v ? `#${v}` : '-'),
    },
    {
      title: '状态',
      dataIndex: 'resolved',
      width: 90,
      render: (v: boolean) => <Tag color={v ? 'green' : 'orange'}>{v ? '已处理' : '未处理'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_: unknown, row: ExceptionEventItem) =>
        !row.resolved ? (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => resolveMutation.mutate(row.id)}
            loading={resolveMutation.isPending}
          >
            处理
          </Button>
        ) : null,
    },
  ]

  return (
    <Card extra={<Button icon={<ReloadOutlined />} onClick={() => refetch()}>刷新</Button>}>
      {isError && (
        <Alert type="error" showIcon message="异常日志加载失败" style={{ marginBottom: 16 }} />
      )}
      <Space wrap style={{ marginBottom: 16 }}>
        <Search
          placeholder="搜索消息/类型"
          allowClear
          style={{ width: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={setSearch}
        />
        <Select
          placeholder="严重级别"
          allowClear
          style={{ width: 120 }}
          value={severity}
          onChange={setSeverity}
        >
          <Option value="warning">warning</Option>
          <Option value="error">error</Option>
          <Option value="critical">critical</Option>
        </Select>
        <Select
          placeholder="处理状态"
          allowClear
          style={{ width: 120 }}
          value={resolved}
          onChange={setResolved}
        >
          <Option value={false}>未处理</Option>
          <Option value={true}>已处理</Option>
        </Select>
      </Space>
      <Table
        columns={columns}
        dataSource={events}
        loading={isLoading}
        rowKey="id"
        pagination={{ showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
        size="small"
      />
    </Card>
  )
}

const Logs: React.FC = () => {
  const tabItems = [
    { key: 'trades', label: '交易日志', children: <TradeLogsTab /> },
    { key: 'exceptions', label: '异常日志', children: <ExceptionLogsTab /> },
    { key: 'system', label: '系统日志', children: <SystemLogsTab /> },
  ]

  return (
    <div className="page-inner">
      <Card title="日志中心">
        <Tabs defaultActiveKey="trades" items={tabItems} />
      </Card>
    </div>
  )
}

export default Logs
